import { useEffect, useState } from "react"
import { Loader2, ExternalLink, AlertCircle } from "lucide-react"
import { Streamdown, type Components as StreamdownComponents } from "streamdown"
import { code } from "@streamdown/code"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  hideTransportBriefViewer,
  selectTransportBriefViewer,
} from "@/reducers/overlaysSlice"
import { AIChatLink } from "./AIChatLink"

const streamdownPlugins = { code }
const streamdownComponents: StreamdownComponents = {
  a: AIChatLink as StreamdownComponents["a"],
}

type FetchState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ok"; markdown: string }
  | { status: "error"; message: string }

export function TransportBriefDialog() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const viewer = useAppSelector(selectTransportBriefViewer)
  const [fetchState, setFetchState] = useState<FetchState>({ status: "idle" })

  // Fetch the digest whenever the dialog opens with a new URL.
  useEffect(() => {
    if (!viewer.visible || !viewer.url) {
      setFetchState({ status: "idle" })
      return
    }

    const controller = new AbortController()
    setFetchState({ status: "loading" })

    fetch(viewer.url, { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(
            i18n.t("ai.transportBrief.httpError", { status: res.status }),
          )
        }
        return res.text()
      })
      .then((text) => {
        setFetchState({ status: "ok", markdown: text })
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        const message =
          err instanceof Error
            ? err.message
            : i18n.t("ai.transportBrief.unknownError")
        setFetchState({ status: "error", message })
      })

    return () => {
      controller.abort()
    }
  }, [viewer.visible, viewer.url])

  const handleOpenChange = (open: boolean) => {
    if (!open) dispatch(hideTransportBriefViewer())
  }

  const title = viewer.title ?? t("ai.transportBrief.defaultTitle")

  return (
    <Dialog open={viewer.visible} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <span aria-hidden>📄</span>
            {title}
          </DialogTitle>
        </DialogHeader>
        <div
          className="flex-1 overflow-y-auto px-6 py-4"
          data-testid="transport-brief-dialog-body"
        >
          {fetchState.status === "loading" && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              <span>{t("ai.transportBrief.loading")}</span>
            </div>
          )}

          {fetchState.status === "error" && (
            <div className="flex flex-col items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              <div className="flex items-center gap-2 font-medium">
                <AlertCircle className="h-4 w-4" />
                {t("ai.transportBrief.loadFailed")}
              </div>
              <p>{fetchState.message}</p>
              {viewer.url && (
                <Button size="sm" variant="outline" asChild className="mt-2">
                  <a
                    href={viewer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="mr-2 h-3.5 w-3.5" />
                    {t("ai.transportBrief.openInNewTab")}
                  </a>
                </Button>
              )}
            </div>
          )}

          {fetchState.status === "ok" && (
            <Streamdown
              className="prose prose-sm max-w-none dark:prose-invert prose-table:my-0"
              plugins={streamdownPlugins}
              components={streamdownComponents}
            >
              {fetchState.markdown}
            </Streamdown>
          )}
        </div>
        {fetchState.status === "ok" && viewer.url && (
          <div className="flex items-center justify-end border-t px-6 py-3">
            <Button size="sm" variant="ghost" asChild>
              <a href={viewer.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-3.5 w-3.5" />
                {t("ai.transportBrief.openSourceInNewTab")}
              </a>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
